import { Link, useParams } from 'react-router-dom';
import { usePayslip } from '../api/hr';
import { formatDate, formatGhs } from '../format';
import { Button, ErrorNote, Icon, Loading } from '../ui/kit';

const monthName = (p: string) => new Date(`${p}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** A print-ready payslip: from the payroll screen (`/payroll/:id/payslip/:employeeId`) or from "My pay" (`/my-pay/:runId`). */
export function Payslip() {
  const { id = '', employeeId = '', runId = '' } = useParams();
  const mine = !!runId;
  const slip = usePayslip(mine ? `/me/payslips/${runId}` : `/payroll/runs/${id}/payslips/${employeeId}`);
  const back = mine ? '/my-pay' : `/payroll/${id}`;

  if (slip.isLoading) return <div className="page"><Loading /></div>;
  if (slip.error || !slip.data) return <div className="page"><ErrorNote error={slip.error} retry={() => void slip.refetch()} /></div>;
  const s = slip.data;
  const earnings: [string, number][] = [['Basic pay', s.basicPaid]];
  if (s.unpaidDeduction) earnings[0] = [`Basic pay (after ${s.unpaidDays} unpaid day${s.unpaidDays === 1 ? '' : 's'})`, s.basicPaid];
  for (const a of s.allowances) earnings.push([a.name, a.amount]);
  if (s.extraEarnings) earnings.push([s.extraNote ?? 'Extra pay', s.extraEarnings]);
  const deductions: [string, number][] = [['Employee pension', s.pensionEmployee], ['Income tax (PAYE)', s.paye]];
  if (s.loanRepayment) deductions.push(['Advance repayment', s.loanRepayment]);
  if (s.otherDeductions) deductions.push([s.otherNote ?? 'Other deduction', s.otherDeductions]);

  return (
    <div className="page receipt-page">
      <div className="no-print row" style={{ justifyContent: 'space-between' }}>
        <Link to={back} className="row small" style={{ textDecoration: 'none' }}>
          <span style={{ width: 18, height: 18 }}><Icon name="back" /></span>
          Back
        </Link>
        <Button variant="primary" onClick={() => window.print()}>Print or save as PDF</Button>
      </div>

      <article className="receipt" aria-label="Payslip">
        <header className="receipt-head">
          <div>
            <h1 className="receipt-hotel">{s.employer.name}</h1>
            {s.employer.address ? <div>{s.employer.address}</div> : null}
            {s.employer.phone ? <div>Tel {s.employer.phone}</div> : null}
          </div>
          <div className="receipt-meta">
            <div className="receipt-title">Payslip</div>
            <div>{monthName(s.period)}</div>
            {s.status === 'PAID' ? <div className="stamp">Paid</div> : null}
          </div>
        </header>

        <section className="receipt-guest">
          <div>
            <div className="eyebrow">Employee</div>
            <strong>{s.employee.name}</strong>
            <div className="num">{s.employee.employeeNo}</div>
          </div>
          <div>
            <div className="eyebrow">Job</div>
            <div>{s.employee.position}</div>
            <div>{s.employee.department}{s.employee.hireDate ? ` · since ${formatDate(s.employee.hireDate, true)}` : ''}</div>
          </div>
        </section>

        <table className="receipt-table">
          <thead>
            <tr><th>Earnings</th><th className="r">GH₵</th></tr>
          </thead>
          <tbody>
            {earnings.map(([k, v], i) => (
              <tr key={`${k}${i}`}><td>{k}</td><td className="r num">{formatGhs(v)}</td></tr>
            ))}
          </tbody>
        </table>
        <table className="receipt-totals">
          <tbody>
            <tr className="strong"><td>Gross pay</td><td className="r num">{formatGhs(s.gross)}</td></tr>
          </tbody>
        </table>

        <table className="receipt-table">
          <thead>
            <tr><th>Deductions</th><th className="r">GH₵</th></tr>
          </thead>
          <tbody>
            {deductions.map(([k, v]) => (
              <tr key={k}><td>{k}</td><td className="r num">−{formatGhs(v)}</td></tr>
            ))}
          </tbody>
        </table>
        <table className="receipt-totals">
          <tbody>
            <tr className="strong"><td>Take-home pay</td><td className="r num">{formatGhs(s.net)}</td></tr>
          </tbody>
        </table>

        <table className="receipt-table">
          <thead>
            <tr><th colSpan={2}>This year so far</th></tr>
          </thead>
          <tbody>
            <tr><td>Gross pay</td><td className="r num">{formatGhs(s.yearToDate.gross)}</td></tr>
            <tr><td>Income tax paid</td><td className="r num">{formatGhs(s.yearToDate.paye)}</td></tr>
            <tr><td>Employee pension</td><td className="r num">{formatGhs(s.yearToDate.pension)}</td></tr>
            <tr><td>Take-home pay</td><td className="r num">{formatGhs(s.yearToDate.net)}</td></tr>
          </tbody>
        </table>
        <footer className="receipt-foot">The employer also pays {formatGhs(s.pensionEmployer)} into your pension this month. Keep this payslip for your records.</footer>
      </article>
    </div>
  );
}
